import { shuffle } from '../utils'
import type { HoursTimeTranslations } from '../../i18n/types'

export type { HoursTimeTranslations }

export type Difficulty = 'easy' | 'medium' | 'hard'
export type GameMode   = 'analog' | 'digital'

export const VALID_MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55] as const
export type ValidMinute = (typeof VALID_MINUTES)[number]

export interface TimeValue {
  hour:   number       // 1-12 for analog, 0-23 for digital
  minute: ValidMinute
}

export interface ClockOption {
  id:   string
  time: TimeValue
}

export interface HoursQuestion {
  id:             string
  displayTime:    TimeValue
  written:        string        // localised written form of displayTime
  writtenOptions: string[]      // non-reverse easy/medium: shuffled written options
  clockOptions:   ClockOption[] // reverse easy/medium: shuffled clock options
}

export const OPTION_COUNT: Record<Difficulty, number> = { easy: 4, medium: 6, hard: 0 }
export const ROUNDS:       Record<Difficulty, number> = { easy: 5, medium: 10, hard: 10 }

// ── Time-to-written helpers ───────────────────────────────────────

function to12h(h: number): number { return h % 12 || 12 }

export function toWrittenTime(hour: number, minute: ValidMinute, time: HoursTimeTranslations): string {
  const h12    = to12h(hour)
  const next12 = to12h(hour + 1)
  const advanceHour = minute >= 35
  const articleH = (advanceHour ? next12 : h12) === 1 ? time.articleSg : time.articlePl
  const template = time.minuteTemplates[minute]
  return template
    .replace('{article}', articleH)
    .replace('{hour}', time.hourNames[h12])
    .replace('{nextHour}', time.hourNames[next12])
    .trim()
}

// ── Time generation ───────────────────────────────────────────────

function randomHour(mode: GameMode): number {
  if (mode === 'analog') return Math.floor(Math.random() * 12) + 1  // 1–12
  return Math.floor(Math.random() * 24)                              // 0–23
}

function randomValidMinute(): ValidMinute {
  return VALID_MINUTES[Math.floor(Math.random() * VALID_MINUTES.length)]
}

function randomTime(mode: GameMode): TimeValue {
  return { hour: randomHour(mode), minute: randomValidMinute() }
}

function uniqueDistractorTimes(correct: TimeValue, mode: GameMode, count: number, time: HoursTimeTranslations): TimeValue[] {
  const used = new Set([toWrittenTime(correct.hour, correct.minute, time)])
  const out: TimeValue[] = []
  let tries = 0
  while (out.length < count && tries < 300) {
    tries++
    const t = randomTime(mode)
    const w = toWrittenTime(t.hour, t.minute, time)
    if (!used.has(w)) { used.add(w); out.push(t) }
  }
  return out
}

function uniqueDistractorWritten(correct: string, mode: GameMode, count: number, time: HoursTimeTranslations): string[] {
  const used = new Set([correct])
  const out: string[] = []
  let tries = 0
  while (out.length < count && tries < 300) {
    tries++
    const t = randomTime(mode)
    const w = toWrittenTime(t.hour, t.minute, time)
    if (!used.has(w)) { used.add(w); out.push(w) }
  }
  return out
}

// ── Question generation ───────────────────────────────────────────

export function generateQuestions(
  difficulty: Difficulty,
  mode:       GameMode,
  isReverse:  boolean,
  timeT:      HoursTimeTranslations,
): HoursQuestion[] {
  const count      = ROUNDS[difficulty]
  const optCount   = OPTION_COUNT[difficulty]
  const questions: HoursQuestion[] = []
  const usedKeys   = new Set<string>()

  for (let i = 0; i < count; i++) {
    let time: TimeValue
    let key:  string
    let tries = 0
    do {
      time = randomTime(mode)
      key  = `${time.hour}:${time.minute}`
      tries++
    } while (usedKeys.has(key) && tries < 200)
    usedKeys.add(key)

    const written = toWrittenTime(time.hour, time.minute, timeT)
    let writtenOptions: string[]      = []
    let clockOptions:   ClockOption[] = []

    if (difficulty !== 'hard') {
      if (isReverse) {
        const distractors = uniqueDistractorTimes(time, mode, optCount - 1, timeT)
        clockOptions = shuffle([time, ...distractors]).map((t, j) => ({
          id:   `q${i}-o${j}`,
          time: t,
        }))
      } else {
        const distractors = uniqueDistractorWritten(written, mode, optCount - 1, timeT)
        writtenOptions = shuffle([written, ...distractors])
      }
    }

    questions.push({ id: `q${i}`, displayTime: time, written, writtenOptions, clockOptions })
  }

  return questions
}

// ── Interactive clock helpers ─────────────────────────────────────

export function snapToNearestValidMinute(rawMinute: number): ValidMinute {
  return VALID_MINUTES.reduce((best, m) =>
    Math.abs(m - rawMinute) < Math.abs(best - rawMinute) ? m : best,
  ) as ValidMinute
}

export function snapToNearestHour12(rawHour: number): number {
  // rawHour is 0–12 float; map to 1–12
  const h = Math.round(rawHour) % 12 || 12
  return h
}

// Accept both AM and PM equivalents for digital reverse hard
export function timesMatch(a: TimeValue, b: TimeValue, mode: GameMode): boolean {
  if (mode === 'analog') return a.hour === b.hour && a.minute === b.minute
  // digital: accept either 12h equivalent
  return (to12h(a.hour) === to12h(b.hour)) && a.minute === b.minute
}

export function normalizeWritten(s: string): string {
  return s.trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/^las?\s+/, '')   // strip leading "la" / "las" article
}
